import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Top-of-page progress bar that reacts to EVERY route change.
 *
 * The video capture (rbgn8zm4_Screen_Recording_20260722) showed a user
 * tapping a mobile-menu link and staring at a blank white screen for 4-5
 * seconds before the Suspense fallback appeared. This bar closes that
 * feedback gap: on any pathname change it starts a shrinking blue strip at
 * the top of the viewport within 16ms so the user never wonders "did my tap
 * register?". It fades out once the new route has actually painted.
 *
 * Zero external deps. Purely inline styles so it's safe even if the
 * Tailwind stylesheet is still loading.
 */
export default function NavigationProgress() {
  const location = useLocation();
  const navType = useNavigationType();
  const firstRender = useRef(true);
  const [phase, setPhase] = useState("idle"); // idle | loading | done
  const [pct, setPct] = useState(0);
  const timeouts = useRef([]);
  const rafs = useRef([]);

  // Kick off the bar on every route change (skip the very first render so
  // it doesn't flash on cold boot when the boot-loader is already handling
  // the visual).
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    // Skip REPLACE (e.g. programmatic redirects that fire on the same tick)
    if (navType === "REPLACE") return;

    // Clear any pending timers/rAFs from the previous transition. Keep the
    // two arrays separate so we never call clearTimeout on a rAF id (harmless
    // in most browsers but still incorrect).
    timeouts.current.forEach(clearTimeout);
    timeouts.current = [];
    rafs.current.forEach(cancelAnimationFrame);
    rafs.current = [];

    setPhase("loading");
    setPct(15);
    // Ramp up toward 85% over ~600ms so the bar always LOOKS like it's
    // moving even on slow networks. It settles at 100% + fades once the
    // new route paints (next effect below).
    timeouts.current.push(setTimeout(() => setPct(45), 100));
    timeouts.current.push(setTimeout(() => setPct(70), 300));
    timeouts.current.push(setTimeout(() => setPct(85), 600));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // When the new page is done rendering (this component re-renders naturally
  // as part of that), snap to 100% then fade out.
  useEffect(() => {
    if (phase !== "loading") return;
    // Use two RAFs so we're past the paint of the new page.
    const id1 = requestAnimationFrame(() => {
      const id2 = requestAnimationFrame(() => {
        setPct(100);
        const t = setTimeout(() => {
          setPhase("done");
          setPct(0);
        }, 300);
        timeouts.current.push(t);
      });
      rafs.current.push(id2);
    });
    rafs.current.push(id1);
    return () => cancelAnimationFrame(id1);
  }, [location.pathname, phase]);

  useEffect(() => () => {
    timeouts.current.forEach(clearTimeout);
    rafs.current.forEach(cancelAnimationFrame);
  }, []);

  if (phase === "idle") return null;
  const opacity = phase === "done" ? 0 : 1;
  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: 9998,
        pointerEvents: "none",
        opacity,
        transition: "opacity 300ms ease",
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${pct}%`,
          background: "linear-gradient(90deg,#2C66E3 0%,#5285F2 50%,#2C66E3 100%)",
          boxShadow: "0 0 10px rgba(44,102,227,0.55), 0 0 4px rgba(44,102,227,0.4)",
          transition: "width 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />
    </div>
  );
}
